import * as Sentry from "@sentry/react";
import { AlertTriangle } from "lucide-react";
import { Button } from "./ui/button.tsx";
import { clearChunkReloadFlag, isChunkLoadError } from "@/lib/lazy-with-retry";

function reloadForStaleChunks() {
  clearChunkReloadFlag();
  window.location.replace(window.location.href);
}

export const ErrorFallback: Sentry.FallbackRender = ({
  error,
  eventId,
  resetError,
}) => {
  const errorMessage = error instanceof Error ? error.message : "";
  const isStaleChunk = isChunkLoadError(error);
  if (__APP_ENV__ !== "production") {
    console.error(error);
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      <div className="p-4 bg-destructive/10 rounded-full mb-4">
        <AlertTriangle className="h-8 w-8 text-destructive" />
      </div>
      <h2 className="text-xl font-semibold mb-2">
        {isStaleChunk ? "Update available" : "Something went wrong"}
      </h2>
      <p className="text-muted-foreground text-center max-w-sm mb-6">
        {isStaleChunk ? (
          <>
            This page is out of date after a recent deploy. Refresh to load the
            latest version.
          </>
        ) : (
          <>
            An unexpected error occurred
            {errorMessage ? `: ${errorMessage}` : ""}.
            <br />
            Please try again shortly or contact support.
          </>
        )}
      </p>
      <div className="flex gap-3">
        {isStaleChunk ? (
          <Button onClick={reloadForStaleChunks}>Refresh page</Button>
        ) : (
          resetError && <Button onClick={resetError}>Try Again</Button>
        )}
      </div>
      <pre className="text-xs text-muted-foreground mt-4">Ref: {eventId}</pre>
    </div>
  );
};

export const ErrorBoundary = ({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback?: React.ReactElement | Sentry.FallbackRender;
}) => {
  return (
    <Sentry.ErrorBoundary fallback={fallback || ErrorFallback}>
      {children}
    </Sentry.ErrorBoundary>
  );
};
